// Swiss stage: one column per round, matches grouped by the record the teams had going
// into the round ("1-0", "0-1"), rounds not paired yet shown as "Waiting for pairing", and
// the standings (W-L-D, Buchholz, round difference; Major: qualified / eliminated).
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TeamLogo from '../teams/TeamLogo';
import MatchCard, { useCardDnd } from './MatchCard';
import './bracket.css';

/** Record of every team before round r: { teamId: 'W-L' or 'W-L-D' }. */
function recordsBefore(matches, r) {
  const rec = {};
  const add = (t, k) => { if (!t) return; rec[t] = rec[t] || { w: 0, l: 0, d: 0 }; rec[t][k]++; };
  for (const m of matches) {
    if (m.round_number >= r || m.status !== 'completed') continue;
    if (m.walkover) { add(m.team1_id || m.team2_id, 'w'); continue; }
    if (m.is_draw) { add(m.team1_id, 'd'); add(m.team2_id, 'd'); continue; }
    if (m.winner_id) { add(m.winner_id, 'w'); add(m.winner_id === m.team1_id ? m.team2_id : m.team1_id, 'l'); }
  }
  return rec;
}

export function SwissStandings({ standings, teams, info, hoverTeam }) {
  const { t } = useTranslation();
  const major = info && info.mode === 'major';
  const anyDraw = (standings || []).some(s => s.draws > 0);
  return (
    <div className="br-card" style={{ padding: '0.7rem 0.8rem', overflowX: 'auto' }}>
      <h3>{t('bracket.standings', 'Standings')}</h3>
      <table className="br-table">
        <thead><tr>
          <th className="num">#</th><th>{t('bracket.team', 'Team')}</th>
          <th className="num">{anyDraw ? t('bracket.wld', 'W-L-D') : t('bracket.wl', 'W-L')}</th>
          <th className="num" title={t('bracket.buchholzHint', 'Sum of the opponents\' wins')}>{t('bracket.buchholz', 'Buchholz')}</th>
          <th className="num" title={t('bracket.rdHint', 'Rounds won minus rounds lost')}>{t('bracket.rd', 'RD')}</th>
          {major && <th />}
        </tr></thead>
        <tbody>
          {(standings || []).map(s => {
            const tm = teams[s.team_id] || { id: s.team_id, name: `#${s.team_id}` };
            return (
              <tr key={s.team_id} className={`${s.qualified ? 'q' : ''}${s.eliminated ? ' e' : ''}${hoverTeam === s.team_id ? ' hl' : ''}`}>
                <td className="num">{s.rank}</td>
                <td className="tm"><TeamLogo team={tm} size={16} radius={4} /><span title={tm.name}>{tm.name}</span></td>
                <td className="num">{s.wins}-{s.losses}{anyDraw ? `-${s.draws}` : ''}</td>
                <td className="num">{s.buchholz}</td>
                <td className="num">{s.round_diff > 0 ? `+${s.round_diff}` : s.round_diff}</td>
                {major && <td>{s.qualified ? <span className="br-tag q">{t('bracket.qualified', 'Qualified')}</span> : s.eliminated ? <span className="br-tag e">{t('bracket.eliminated', 'Eliminated')}</span> : null}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function SwissView({ bracket, matches, teams, standings, readOnly = false, selectedId, onSelect, onSwap, linkPrefix = '/match' }) {
  const { t } = useTranslation();
  const [hoverTeam, setHoverTeam] = useState(null);
  const dnd = useCardDnd(readOnly ? null : onSwap, matches);
  const info = bracket.swiss_info || {};
  const total = Math.max(info.total_rounds || 0, matches.reduce((mx, m) => Math.max(mx, m.round_number), 0));
  const anyDraw = matches.some(m => m.is_draw);

  const cols = useMemo(() => Array.from({ length: total }, (_, i) => {
    const r = i + 1;
    const list = matches.filter(m => m.round_number === r).sort((a, b) => a.position - b.position);
    const rec = recordsBefore(matches, r);
    const label = (tid) => { const x = rec[tid] || { w: 0, l: 0, d: 0 }; return anyDraw ? `${x.w}-${x.l}-${x.d}` : `${x.w}-${x.l}`; };
    const groups = [];
    for (const m of list) {
      const k = label(m.team1_id || m.team2_id);
      let g = groups.find(x => x.k === k);
      if (!g) { g = { k, list: [] }; groups.push(g); }
      g.list.push(m);
    }
    groups.sort((a, b) => { const [aw, al] = a.k.split('-').map(Number), [bw, bl] = b.k.split('-').map(Number); return (bw - aw) || (al - bl); });
    return { r, groups, bo: list[0] ? list[0].best_of : null };
  }), [matches, total, anyDraw]);

  return (
    <div className="br-stage">
      <div className="br-card" style={{ padding: '0.7rem 0.8rem', minWidth: 0 }}>
        <div className="br-swiss">
          {cols.map(c => (
            <div key={c.r} className="br-swiss-col">
              <div className="head">
                <div className="n">{t('bracket.round.n', 'Round {{n}}', { n: c.r })}</div>
                <div className="b">{c.bo ? `BO${c.bo}` : ' '}</div>
              </div>
              {c.groups.length === 0 && <div className="br-wait">{t('bracket.waitingPairing', 'Waiting for pairing')}</div>}
              {c.groups.map(g => (
                <React.Fragment key={g.k}>
                  {c.r > 1 && <div className="br-rec">{g.k}</div>}
                  {g.list.map(m => (
                    <MatchCard key={m.id} m={m} teams={teams} readOnly={readOnly} selected={selectedId === m.id} onSelect={onSelect}
                      linkPrefix={linkPrefix} dnd={dnd} hoverTeam={hoverTeam} setHoverTeam={setHoverTeam} />
                  ))}
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>
      <SwissStandings standings={standings} teams={teams} info={info} hoverTeam={hoverTeam} />
    </div>
  );
}
